<template>
  <v-container fluid class="pa-0">
    <EntityIndex
        :loading="loading"
        :total-count="totalCount"
        :table-headers="tableHeaders"
        :table-rows="tableRows"
        :table-options.sync="tableOptions"
        :selected-rows.sync="selectedRows"
        :selected-rows-actions="selectedRowsActions"
        :no-data-text="$t('corporation.noCorporations')"
        item-key="corporation.id">

      <template v-slot:actions>
        <v-btn v-if="$featurePermissions.corporationCRUD"
               color="primary"
               elevation="2"
               class="mb-2"
               @click="$refs.corporationDialog.openCreateNewCorporation()">
          <v-icon>mdi-plus</v-icon>
          {{ $t('actions.add') }}
        </v-btn>
      </template>


      <template v-slot:filter>
        <entity-view-filter>
          <template v-slot:filter1>
            <v-checkbox :label="$t('actions.showDeleted')"
                        v-model="filterIncludeDeleted"
                        @change="fetch"/>
          </template>


        </entity-view-filter>
      </template>

      <template v-slot:item.name="{ item }">
        <router-link :to="item.routerLinkTo">{{ item.corporation.name }}</router-link>
      </template>


      <template v-slot:entityListItem="props">
        <corporation-list-item
            :item="props.item"
            :show-actions="true"
            :row-click-enabled="true"/>
      </template>

    </EntityIndex>


    <entity-confirm-delete-dialog
        ref="confirmDeleteDialog"
        :text="$t('corporation.deleteConfirm')"
        :text-plural="$t('corporation.deleteConfirmPlural')"
        @confirmedDelete="deleteCorporations">
      <template v-slot:entityListItem="props">
        <corporation-list-item :item="props.item"/>
      </template>
    </entity-confirm-delete-dialog>

    <entity-confirm-undelete-dialog
        ref="confirmUndeleteDialog"
        :text="$t('corporation.undeleteConfirm')"
        :text-plural="$t('corporation.undeleteConfirmPlural')"
        @confirmedUndelete="undeleteCorporations">
      <template v-slot:entityListItem="props">
        <corporation-list-item :item="props.item"/>
      </template>
    </entity-confirm-undelete-dialog>

    <corporation-dialog ref="corporationDialog" @done="corporationCreatedEditedOrDeleted"/>
  </v-container>

</template>

<style scoped>
</style>

<script>
import EntityViewFilter from '@/components/entity-crud/EntityViewFilter';
import i18n from '@/i18n';
import EntityIndex from '@/components/entity-crud/EntityIndex';
import EntityConfirmDeleteDialog from '@/components/entity-crud/EntityDeleteConfirmDialog';
import CorporationsService from '@/services/CorporationsService';
import CorporationDialog from '@/views/corporations/CorporationDialog';
import CorporationListItem from '@/views/corporations/CorporationListItem';
import EntityConfirmUndeleteDialog from '@/components/entity-crud/EntityUndeleteConfirmDialog';

export default {
  components: {
    EntityConfirmUndeleteDialog,
    CorporationListItem,
    CorporationDialog,
    EntityConfirmDeleteDialog,
    EntityIndex,
    EntityViewFilter,
  },
  name: 'CorporationIndex',
  data() {
    return {
      corporations: [],
      selectedRows: [],
      loading: false,
      totalCount: 0,
    };
  },
  computed: {

    filterIncludeDeleted: {
      get() {
        return this.$store.state.corporationIndexState.filterIncludeDeleted;
      },
      set(value) {
        this.$store.commit('corporationIndexState/setFilterIncludeDeleted', value);
      },
    },

    tableOptions: {
      get() {
        return this.$store.state.corporationIndexState.tableOptions;
      },
      set(value) {
        this.$store.commit('corporationIndexState/setTableOptions', value);
      },
    },


    tableHeaders() {
      let headers = [
        {
          value: 'name',
          text: i18n.t('corporation.name'),
          getValue: corporation => {
            return corporation.name;
          },
        },
        {
          value: 'allowCsvExport',
          text: i18n.t('corporation.allowCsvExportTableHeader'),
          sortable: false,
          getValue: corporation => {
            return this.$options.filters.yesNo(corporation.allowCsvExport);
          },
        },
        {
          value: 'deletedStatus',
          text: '',
          sortable: false,
          hidden: !this.filterIncludeDeleted,
          getValue: corporation => {
            if (corporation.deleted) {
              return this.$t('entityTable.deletedItem');
            }
            return null;
          },
        },
        {
          value: 'rowActions',
          text: '',
          getValue: corporation => {
            return this.createRowActions(corporation);
          },
        },
      ];
      return headers.filter(header => !header.hidden);
    },

    tableRows() {
      return this.corporations.map(corporation => {
        let row = {};
        row.corporation = corporation;
        row.routerLinkTo = `corporations/${corporation.id}`;
        row.deleted = corporation.deleted;

        row.rowClick = () => {
          this.$router.push(row.routerLinkTo);
        };

        this.tableHeaders.forEach(header => {
          if (header.getValue) {
            row[header.value] = header.getValue(corporation);
          }
        });
        return row;
      });
    },

    selectedRowsActions() {
      const actions = [
        {
          label: i18n.t('actions.delete'),
          icon: 'mdi-delete',
          disabled: this.selectedRows.length === 0,
          hasPermission: this.$featurePermissions.corporationCRUD,
          click: () => {
            this.confirmDelete(this.selectedRows);
          },
        },
      ];
      return actions.filter(action => action.hasPermission);
    },
  },
  methods: {
    createRowActions(corporation) {

      let actions = [
        {
          label: i18n.t('actions.show'),
          icon: 'mdi-view-list',
          hasPermission: this.$featurePermissions.corporationCRUD,
          click: () => {
            this.$router.push(`corporations/${corporation.id}`);
          },
        },
        {
          label: i18n.t('actions.modify'),
          icon: 'mdi-pencil-outline',
          hasPermission: this.$featurePermissions.corporationCRUD,
          click: () => {
            this.openEditDialog(corporation.id);
          },
        },

      ];


      if (!corporation.deleted) {
        let deleteAction = {
          label: i18n.t('actions.delete'),
          icon: 'mdi-delete',
          dividerBefore: true,
          hasPermission: this.$featurePermissions.corporationCRUD,
          click: () => {
            this.confirmDelete([{ corporation: corporation }]);
          },
        };
        actions.push(deleteAction);
      } else {
        let undeleteAction = {
          label: i18n.t('actions.undelete'),
          icon: 'mdi-delete-restore',
          dividerBefore: true,
          hasPermission: this.$featurePermissions.corporationCRUD,
          click: () => {
            this.confirmUndelete([{ corporation: corporation }]);
          },
        };
        actions.push(undeleteAction);
      }

      return actions.filter(action => action.hasPermission);
    },

    confirmDelete(items) {
      this.$refs.confirmDeleteDialog.show(items);
    },
    deleteCorporations(items) {
      const corporationIds = items.map(item => item.corporation.id);
      CorporationsService.delete(corporationIds)
          .finally(() => {
            this.$refs.confirmDeleteDialog.close();
            this.corporationCreatedEditedOrDeleted();
          });
    },

    confirmUndelete(items) {
      this.$refs.confirmUndeleteDialog.show(items);
    },
    undeleteCorporations(items) {
      const corporationIds = items.map(item => item.corporation.id);
      CorporationsService.undelete(corporationIds)
          .finally(() => {
            this.$refs.confirmUndeleteDialog.close();
            this.corporationCreatedEditedOrDeleted();
          });
      this.updateCorporationSelect();
    },

    openEditDialog(corporationId) {
      this.$refs.corporationDialog.openEditForCorporation(corporationId);
    },

    corporationCreatedEditedOrDeleted(){
      this.fetch();
      this.updateCorporationSelect();
    },

    fetch() {
      this.loading = true;
      this.selectedRows = [];
      CorporationsService.list({
            filterIncludeDeleted: this.filterIncludeDeleted,
            limit: this.tableOptions.itemsPerPage,
            offset: (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage,
            orderBy: this.tableOptions.sortBy[0],
            order: this.tableOptions.sortDesc[0] ? 'DESC' : 'ASC',
          },
      ).then(result => {
        this.loading = false;
        this.corporations = result.data.items;
        this.totalCount = result.data.totalCount;
      }).catch(result => {
        console.log(JSON.stringify(result));
        this.loading = false;
      });
    },

    updateCorporationSelect(){
      this.$store.dispatch('userSessionStateStore/update');
    },
  },

  watch: {
    tableOptions: {
      handler() {
        this.fetch();
      },
      deep: true,
    },
  },
  created() {
    this.fetch();
  },
};
</script>
